import {
  Alert,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "../../util/axios";
import Token from "../../util/Token";
import { BaseTable } from "../BaseTable/BaseTable";
import { useNavigate, useParams, useMatch, Link as RouterLink } from "react-router-dom";
import Page from "../Page";
import * as React from "react";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import {
  useGetDetail,
  useGetMeterValues,
  useMutate,
} from "src/hooks/useFetch.hook";
import { fDateTime, onlyTime, fTransactionYearFirstDateTime } from "src/utils/formatTime";
import { convertUTCToLocal } from "src/utils/dateTimeUtils";
import { format } from "date-fns";
import { BackButton } from "src/common/components/BackButton";
import { useDomainConfig } from "src/hooks/useDomainConfig";
import { useAccess } from "src/hooks/useAccess.hook";
import sidebarConfig from "src/layouts/dashboard/SidebarConfig";
import enums, { FEATURE } from "src/util/enums";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import { Loader } from "../Loader";
import {
  chartLabelConstants,
  chartTypeToShow,
} from "./TransactionsList/TransactionsList.constants";
import { BaseChart } from "../charts/BaseOptionChart";
import { is } from "src/util/is";
import { Form, FormikProvider, useFormik } from "formik";
import { transactionStatusValidationSchema } from "src/util/validationSchemas";
import TransactionStatus from "../../data/transactionStatus";
import { getTeamUrl, transactionIdUrl } from "src/react-query/endPoints";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { LoadingButton } from "@mui/lab";
import { Icon } from "@iconify/react";
import downIcon from "@iconify/icons-eva/arrow-down-fill";
import upIcon from "@iconify/icons-eva/arrow-up-fill";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { fChargerLogDateTime, yearFirstDateTimeFormat } from "src/utils/formatTime";

const getStatusColor = (status = "") => {
  const s = status.toLowerCase();
  if (s === "available" || s === "started" || s === "active") return "success";
  if (s === "charging") return "primary";
  if (s === "preparing" || s === "finishing") return "info";
  if (s === "faulted" || s === "failed") return "error";
  if (s.includes("suspended") || s === "unavailable" || s === "stopped") return "warning";
  return "default";
};

const txStatusTextCell = (value) =>
  value ? (
    <Tooltip title={value} placement="top">
      <Typography
        variant="body2"
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}
      >
        {value}
      </Typography>
    </Tooltip>
  ) : (
    <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>—</Typography>
  );

const txStatusHistoryColumns = [
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    minWidth: 150,
    renderCell: ({ value }) => (
      <Chip
        label={value || "—"}
        size="small"
        color={getStatusColor(value)}
        variant="filled"
        sx={{ fontWeight: 600, fontSize: "0.75rem" }}
      />
    ),
  },
  {
    field: "timestamp",
    headerName: "Timestamp",
    flex: 1.3,
    minWidth: 190,
    renderCell: ({ value }) =>
      value ? (
        <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {fChargerLogDateTime(value)}
          </Typography>
        </Tooltip>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>—</Typography>
      ),
  },
  {
    field: "error_code",
    headerName: "Error Code",
    flex: 1,
    minWidth: 150,
    renderCell: ({ value }) =>
      value && value !== "NoError" ? (
        <Chip label={value} size="small" color="error" variant="outlined" sx={{ fontWeight: 600, fontSize: "0.75rem" }} />
      ) : (
        <Chip label="No Error" size="small" color="success" variant="outlined" sx={{ fontWeight: 500, fontSize: "0.75rem" }} />
      ),
  },
  {
    field: "error_info",
    headerName: "Error Info",
    flex: 1,
    minWidth: 140,
    renderCell: ({ value }) => txStatusTextCell(value),
  },
  {
    field: "vendor_id",
    headerName: "Vendor ID",
    flex: 1,
    minWidth: 140,
    renderCell: ({ value }) => txStatusTextCell(value),
  },
  {
    field: "vendor_error_code",
    headerName: "Vendor Error Code",
    flex: 1,
    minWidth: 160,
    renderCell: ({ value }) => txStatusTextCell(value),
  },
];


const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1.25),
  textAlign: "start",
  color: theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius * 0.8,
  background: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: `0 2px 10px ${theme.palette.grey[500]}15`,
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    // Hover effects removed
  },
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    borderRadius: `${theme.shape.borderRadius * 0.8}px ${theme.shape.borderRadius * 0.8}px 0 0`,
  },
}));

export const GridLabel = ({ label, value, color, xs, sm, md, children }) => (
  <Grid item xs={xs} sm={sm} md={md} sx={{ display: 'flex' }}>
    <Item sx={{ width: '100%', height: '100%' }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "68px", // More compact card height
          minHeight: "68px",
        }}
      >
        <h4 style={{
          margin: "0 0 4px 0",
          fontSize: "0.75rem",
          fontWeight: 700,
          color: "#333",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          lineHeight: "1.1",
          flex: "0 0 auto"
        }}>
          {label}
        </h4>
        <div style={{
          flex: "1 1 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start"
        }}>
          {children ? (
            children
          ) : (
            <p style={{

              color: color ?? "#111",
              margin: 0,
              fontSize: "1.1rem",
              fontWeight: 400,
              wordBreak: "break-word",
              lineHeight: "1.3"
            }}>
              {value ?? "--"}
            </p>
          )}
        </div>
      </div>
    </Item>
  </Grid>
);

const graphDataAccumulator = (dataArr) => {
  let accuObj = dataArr.reduce((accuObj, current) => {
    let val = is.empty(current.value) ? 0 : current.value;
    if (accuObj[current.measurand]) {
      accuObj[current.measurand] = {
        ...accuObj[current.measurand],
        xaxis: [
          ...accuObj[current.measurand]["xaxis"],
          new Date(current.value_timestamp).getTime(),
        ],
        yaxis: [...accuObj[current.measurand]["yaxis"], val],
      };
    } else {
      let initialTime = new Date(current.value_timestamp);
      initialTime.setMinutes(initialTime.getMinutes() - 5);

      accuObj[current.measurand] = {
        xaxis:
          current.measurand !== "Temperature"
            ? [new Date(current.value_timestamp).getTime()]
            : [
              initialTime.getTime(),
              new Date(current.value_timestamp).getTime(),
            ],
        yaxis: current.measurand !== "Temperature" ? [val] : [0, val],
        unit: current.unit,
        label: chartLabelConstants[current.measurand],
        measurand: current.measurand,
      };
    }
    return accuObj;
  }, {});
  const possibleMeasurands = Object.keys(accuObj);
  accuObj = { ...accuObj, possibleMeasurands };
  return accuObj;
};

export const TransactionView = () => {
  const { id } = useParams();
  const isRefundDetail = useMatch("/refund/:id");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { teamId } = useSelectedTeam();
  const hasViewAccess = useAccess(FEATURE.TRANSACTION, "V");
  const hasReadOnlyAccess = useAccess(FEATURE.TRANSACTION, "R");
  const hasAllAccess = useAccess(FEATURE.TRANSACTION, "FA");
  const { mutate, isLoading } = useMutate("transactionStatus", { id });
  const domainConfig = useDomainConfig();

  const [initialAmountLabel, setInitialAmountLabel] = React.useState("");
  const [remainingAmountLabel, setRemainingAmountLabel] = React.useState("");
  const [showMeterValues, setShowMeterValues] = React.useState(false);
  const [chartData, setChartData] = React.useState([]);

  const [txStatusHistory, setTxStatusHistory] = React.useState([]);
  const [txStatusHistoryLoading, setTxStatusHistoryLoading] = React.useState(false);
  const [txStatusHistoryError, setTxStatusHistoryError] = React.useState("");
  const [txStatusHistoryPage, setTxStatusHistoryPage] = React.useState(0);
  const [txStatusHistoryPageSize, setTxStatusHistoryPageSize] = React.useState(20);
  const [txStatusHistoryTotal, setTxStatusHistoryTotal] = React.useState(0);

  const normalizedTxStatusHistory = React.useMemo(
    () =>
      txStatusHistory.map((item, index) => ({
        id: index,
        status: item.status || "",
        timestamp: item.status_timestamp || item.timestamp || item.created_at || "",
        error_code: item.error_code || "",
        error_info: item.error_info || "",
        vendor_id: item.vendor_id || "",
        vendor_error_code: item.vendor_error_code || "",
      })),
    [txStatusHistory]
  );

  const fetchTxStatusHistory = React.useCallback(
    async (page = 0, pageSize = 20) => {
      if (!id) return;
      try {
        Token.setAuthToken();
        setTxStatusHistoryLoading(true);
        setTxStatusHistoryError("");
        const response = await axios.get(`/cms/v1/connector/status-history/transaction/${id}`, {
          params: { page: page + 1, pageSize },
        });
        const history = response?.data?.data || response?.data || [];
        const total =
          response?.data?.pagination?.total ??
          response?.data?.total ??
          (Array.isArray(history) ? history.length : 0);
        setTxStatusHistory(Array.isArray(history) ? history : []);
        setTxStatusHistoryTotal(total);
      } catch (error) {
        setTxStatusHistoryError(error?.message || "Failed to fetch status history.");
      } finally {
        setTxStatusHistoryLoading(false);
      }
    },
    [id]
  );

  React.useEffect(() => {
    fetchTxStatusHistory(txStatusHistoryPage, txStatusHistoryPageSize);
  }, [fetchTxStatusHistory, txStatusHistoryPage, txStatusHistoryPageSize]);

  // Invoice Generation Function (HTML to PDF)
  const generateInvoicePDF = () => {
    if (!transaction) return;

    const startTime = fDateTime(transaction.starttime, "yyyy-MM-dd HH:mm");
    const stopTime = transaction.stoptime ? fDateTime(transaction.stoptime, "yyyy-MM-dd HH:mm") : 'Ongoing';
    const startValue = parseFloat(transaction.start_meter_value || 0);
    const stopValue = parseFloat(transaction.stop_meter_value || 0);
    const currentValue = parseFloat(transaction.current_meter_value || 0);

    // Use stop meter value if available, otherwise use current meter value
    const endValue = stopValue > 0 ? stopValue : currentValue;
    const energyConsumed = endValue > startValue ? (endValue - startValue) / 1000 : 0;

    // Construct full logo URL
    const logoUrl = domainConfig.logoUrl.startsWith('http')
      ? domainConfig.logoUrl
      : `${window.location.origin}/${domainConfig.logoUrl}`;

    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${transaction.transactionId}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
          }
          .invoice-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 40px;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
          .header {
            background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
            color: white;
            padding: 30px;
            margin: -40px -40px 30px -40px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .header-left {
            display: flex;
            align-items: center;
            gap: 20px;
          }
          .logo {
            width: 60px;
            height: 60px;
            background: white;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            border: 2px solid rgba(255,255,255,0.3);
            overflow: hidden;
          }
          .logo img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
          .header h1 {
            margin: 0;
            font-size: 32px;
            font-weight: bold;
          }
          .company-info {
            text-align: right;
            font-size: 14px;
          }
          .invoice-details {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            padding: 20px;
            background-color: #f8f9fa;
            border-radius: 8px;
          }
          .invoice-details h3 {
            margin: 0 0 10px 0;
            color: #1976d2;
          }
          .invoice-details p {
            margin: 5px 0;
            color: #666;
          }
          .customer-info, .station-info {
            flex: 1;
            margin-right: 20px;
          }
          .transaction-table {
            width: 100%;
            border-collapse: collapse;
            margin: 30px 0;
            background: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .transaction-table th {
            background: #1976d2;
            color: white;
            padding: 15px;
            text-align: left;
            font-weight: bold;
          }
          .transaction-table td {
            padding: 15px;
            border-bottom: 1px solid #eee;
          }
          .transaction-table tr:last-child td {
            border-bottom: none;
          }
          .summary {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            margin: 20px 0;
          }
          .summary h3 {
            margin: 0 0 15px 0;
            color: #1976d2;
          }
          .summary p {
            margin: 8px 0;
            color: #666;
          }
          .total-amount {
            background: #e3f2fd;
            padding: 20px;
            border-radius: 8px;
            text-align: right;
            margin: 20px 0;
          }
          .total-amount h3 {
            margin: 0 0 10px 0;
            color: #1976d2;
          }
          .total-amount .amount {
            font-size: 24px;
            font-weight: bold;
            color: #1976d2;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            padding: 20px;
            background: #f8f9fa;
            border-radius: 8px;
            color: #666;
          }
          @media print {
            body { background: white; }
            .invoice-container { box-shadow: none; margin: 0; padding: 0; }
            .header { margin: 0 0 30px 0; }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="header">
            <div class="header-left">
              <div class="logo">
                <img src="${logoUrl}" alt="Company Logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
                <div style="display: none; width: 100%; height: 100%; align-items: center; justify-content: center; font-size: 24px; font-weight: bold; color: #1976d2; background: white; border-radius: 8px;">A</div>
              </div>
              <h1>INVOICE</h1>
            </div>
            <div class="company-info">
              <div><strong>${domainConfig.title}</strong></div>
              <div>Email: ${domainConfig.email}</div>
            </div>
          </div>

          <div class="invoice-details">
            <div class="customer-info">
              <h3>Bill To:</h3>
              <p><strong>Email:</strong> ${transaction.email || 'N/A'}</p>
              <p><strong>Phone:</strong> ${transaction.phone || 'N/A'}</p>
            </div>
            <div class="station-info">
              <h3>Charging Station:</h3>
              <p><strong>Station:</strong> ${transaction.chargepoint_name || 'N/A'}</p>
              <p><strong>Station ID:</strong> ${transaction.chargepoint_id || 'N/A'}</p>
              <p><strong>Connector:</strong> ${transaction.connectorId || 'N/A'}</p>
            </div>
          </div>

          <div style="margin: 20px 0;">
            <p><strong>Invoice #:</strong> ${transaction.transactionId}</p>
            <p><strong>Date:</strong> ${fDateTime(transaction.starttime, "yyyy-MM-dd")}</p>
            <p><strong>Transaction ID:</strong> ${transaction.transactionId}</p>
          </div>

          <table class="transaction-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Start Time</th>
                <th>Stop Time</th>
                <th>Energy (kWh)</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>EV Charging Session</td>
                <td>${startTime}</td>
                <td>${stopTime}</td>
                <td>${energyConsumed.toFixed(2)}</td>
                <td>₹${(Number(transaction.totalAmountCharged) || 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td colspan="4" style="text-align: right; font-weight: bold;">Consumption Cost:</td>
                <td>₹${((Number(transaction.totalAmountCharged) || 0) - (Number(transaction.taxAmount) || 0) - (Number(transaction.transactionAmount) || 0)).toFixed(2)}</td>
              </tr>
              <tr>
                <td colspan="4" style="text-align: right; font-weight: bold;">PG Charges:</td>
                <td>₹${(Number(transaction.transactionAmount) || 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td colspan="4" style="text-align: right; font-weight: bold;">Tax Amount:</td>
                <td>₹${(Number(transaction.taxAmount) || 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td colspan="4" style="text-align: right; font-weight: bold;">Total Charging Cost:</td>
                <td>₹${(Number(transaction.totalAmountCharged) || 0).toFixed(2)}</td>
              </tr>
              ${transaction.totalRefund && transaction.totalRefund > 0 ? `
              <tr style="color: #d32f2f;">
                <td colspan="4" style="text-align: right; font-weight: bold;">
                Refund Amount:
                </td>
                <td style="color: #d32f2f;">-₹${(Number(transaction.totalRefund) || 0).toFixed(2)}</td>
              </tr>
              ` : ''}
              <tr style="background-color: #f8f9fa;">
                <td colspan="4" style="text-align: right; font-weight: bold;">
                Total Paid Amount:
                </td>
                <td style="font-weight: bold;">₹${(Number(transaction.totalAmountPaid) || 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <div class="summary">
            <h3>Summary</h3>
            <p><strong>Start Meter Reading:</strong> ${transaction.start_meter_value || 'N/A'} Wh</p>
            <p><strong>Stop Meter Reading:</strong> ${transaction.stop_meter_value || 'N/A'} Wh</p>
            <p><strong>Payment Method:</strong> ${transaction?.paymentDescription === "QRv2 Payment" || transaction?.paymentDescription?.toUpperCase().includes("UPI") ? "UPI" : transaction.payment_type || "N/A"}</p>
            <p><strong>Stop Reason:</strong> ${transaction.stop_reason || 'N/A'}</p>
            ${transaction.totalRefund && transaction.totalRefund > 0 ? `
            <p><strong>Refund Status:</strong> ${transaction.refundStatus || 'Processed'}</p>
            ` : ''}
          </div>

          <div class="summary">
            <h3>Amount Breakdown</h3>
            ${transaction.payment_type && (transaction.payment_type.toLowerCase().includes('wallet') || transaction.payment_type.toLowerCase().includes('balance')) ? `
            <p><strong>Total Amount Paid:</strong> ₹${(Number(transaction.totalAmountPaid) || 0).toFixed(2)}</p>
            ` : `
            <p><strong>Amount Paid:</strong> ₹${(Number(transaction.totalAmountPaid) || 0).toFixed(2)}</p>
            `}
            <p><strong>Consumption Cost:</strong> ₹${((Number(transaction.totalAmountCharged) || 0) - (Number(transaction.taxAmount) || 0) - (Number(transaction.transactionAmount) || 0)).toFixed(2)}</p>
            <p><strong>Tax Amount:</strong> ₹${(Number(transaction.taxAmount) || 0).toFixed(2)}</p>
            <p><strong>PG Charges:</strong> ₹${(Number(transaction.transactionAmount) || 0).toFixed(2)}</p>
            <p><strong>Total Charging Cost:</strong> ₹${(Number(transaction.totalAmountCharged) || 0).toFixed(2)}</p>
            ${transaction.totalRefund && transaction.totalRefund > 0 ? `
            <p><strong>Refund Amount: ₹${(Number(transaction.totalRefund) || 0).toFixed(2)}</strong></p>
            ` : ''}
          </div>

          <div class="summary">
            <h3>Payment Information</h3>
            <p><strong>Payment Method:</strong> ${transaction?.paymentDescription === "QRv2 Payment" || transaction?.paymentDescription?.toUpperCase().includes("UPI") ? "UPI" : transaction.payment_type || 'N/A'}</p>
            ${transaction.payment_type && (transaction.payment_type.toLowerCase().includes('wallet') || transaction.payment_type.toLowerCase().includes('balance')) ? `
            <p><strong>Total Amount Paid:</strong> ₹${(Number(transaction.totalAmountPaid) || 0).toFixed(2)}</p>
            <p><strong>Refund Amount:</strong> ₹${((Number(transaction.totalAmountPaid) || 0) - (Number(transaction.totalAmountCharged) || 0)).toFixed(2)}</p>
            ` : `
            <p><strong>Total Paid Amount:</strong> ₹${(Number(transaction.totalAmountPaid) || 0).toFixed(2)}</p>
            `}
          </div>


          <div class="footer">
            <p><strong>Thank you for using ${domainConfig.name} Charging Services!</strong></p>
            <p>For support, contact: ${domainConfig.email}</p>
          </div>
        </div>
      </body>
      </html>
    `;

    // Create a new window with the invoice HTML
    const printWindow = window.open('', '_blank');
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();

    // Wait for the content to load, then trigger print dialog
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const onSubmit = (transactionStatusObj, { setSubmitting }) => {
    const {
      actions: { setAlert },
    } = alertSlice;
    const { status } = transactionStatusObj;
    if (status === "None") {
      dispatch(
        setAlert({
          open: true,
          message: "Please select a status first",
          severity: enums.SEVERITY.ERROR,
        })
      );
      setSubmitting(false);
      return;
    }
    const mutateObject = {
      url: getTeamUrl(teamId, `${transactionIdUrl(id)}/status`),
      method: "PATCH",
    };

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: transactionStatusObj,
      },
      {
        onSuccess: () => {
          dispatch(
            setAlert({
              open: true,
              message: "Succesfully updated the transaction status",
              severity: enums.SEVERITY.SUCCESS,
            })
          );
          // navigate(sidebarConfig[FEATURE.TRANSACTION].path)
        },
      }
    );
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: { notes: "", status: "" },
    validationSchema: transactionStatusValidationSchema,
    onSubmit: onSubmit,
  });

  const {
    errors,
    touched,
    values,
    setFieldValue,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
  } = formik;

  React.useEffect(() => {
    // Only check authorization if we have user data loaded
    // This prevents false negatives when opening in new tabs
    if (teamId && hasViewAccess === false) {
      dispatch(
        alertSlice.actions.setAlert({ open: true, message: "Not Authorized" })
      );
      navigate(sidebarConfig[FEATURE.TRANSACTION].path);
    }
  }, [hasViewAccess, navigate, dispatch, teamId]);

  const { data, isFetching } = useGetDetail(
    isRefundDetail ? "refund" : "transaction",
    {
      id,
    }
  );
  const [transaction] = data ?? [];
  const { data: meterValues } =
    useGetMeterValues("transactionMeterValue", {
      showMeterValues,
      transactionId: transaction ? transaction?.transactionId : null,
      connectorId: transaction ? transaction?.connectorId : null,
    });

  React.useEffect(() => {
    if (!is.empty(transaction) && !isFetching) {
      const { status, notes, payment_type } = transaction;
      let initialAmtLabel = "";
      let remainingAmtLabel = "";
      switch (payment_type) {
        case "WALLET":
          initialAmtLabel = "Total Amount Paid";
          remainingAmtLabel = "Refund Amount";
          break;
        case "CARD":
          initialAmtLabel = "Total Paid Amount";
          remainingAmtLabel = "Refund Amount";
          break;
        case "PREPAID":
          initialAmtLabel = "Initial Card Balance";
          remainingAmtLabel = "Remaining Card Balance";
          break;
        case "RFID":
        default:
          initialAmtLabel = "Total Amount Paid";
          remainingAmtLabel = "Total Refund";
          break;
      }

      const formValues = {
        status: status ? status : "None",
        notes: notes ? notes : "",
      };
      resetForm({ values: formValues });
      setRemainingAmountLabel(remainingAmtLabel);
      setInitialAmountLabel(initialAmtLabel);
    }
  }, [transaction, resetForm, isFetching]);

  React.useEffect(() => {
    if (showMeterValues && meterValues) {
      const chartValues = graphDataAccumulator(meterValues);
      const measurandsArr = chartValues.possibleMeasurands;
      const finalData = measurandsArr.map(
        (measurand) => chartValues[measurand]
      );
      setChartData(finalData);
    }
  }, [showMeterValues, meterValues]);

  // Show loader while authentication state is being determined
  if (isFetching || !teamId) {
    return <Loader />;
  }

  const viewLogs = () => {
    // Use our timezone utility function to handle conversion properly
    // This function handles both Date objects and string timestamps correctly
    const localStartTime = fTransactionYearFirstDateTime(transaction?.starttime);

    // For stop time, convert UTC to local, add 2 minutes, then format
    // This ensures proper timezone handling without double conversion
    const stopTimeDate = transaction?.stoptime;
    let localStopTime;
    if (stopTimeDate) {
      // Convert UTC string to local Date object
      const localStopDate = convertUTCToLocal(stopTimeDate);
      if (localStopDate) {
        // Add 2 minutes (120000 ms) to the local date
        const stopTimeWithTwoMinutes = new Date(localStopDate.getTime() + 120000);
        // Format using the same format as fTransactionYearFirstDateTime
        localStopTime = format(stopTimeWithTwoMinutes, "yyyy-MM-dd hh:mm:ss a");
      } else {
        localStopTime = fTransactionYearFirstDateTime(stopTimeDate);
      }
    } else {
      localStopTime = fTransactionYearFirstDateTime(stopTimeDate);
    }

    const logsData = {
      ...transaction,
      starttime: localStartTime,
      stoptime: localStopTime,
    };

    dispatch(tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
      data: { dataForLogs: logsData },
      tabOpenedFor: "charger",
      tabOpened: "logs"
    }))
    navigate(`/charger/${transaction?.charge_box_pk}`)
  };

  return (
    <Page
      title={`${!isRefundDetail ? "Transactions" : "Refund"
        }`}
    >
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }}>
        <BackButton
          text={`${!isRefundDetail ? "Transaction" : "Refund"} details`}
          path={`${!isRefundDetail ? "/transactions" : "/refund"}`}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            onClick={generateInvoicePDF}
            startIcon={<Icon icon="eva:file-text-outline" />}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
              boxShadow: '0 4px 14px 0 rgba(46, 125, 50, 0.39)',
              fontWeight: 700,
              letterSpacing: '0.5px',
              px: 2,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
                boxShadow: '0 6px 20px 0 rgba(46, 125, 50, 0.5)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            Generate Invoice
          </Button>
          <Button
            variant="contained"
            onClick={viewLogs}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.39)',
              fontWeight: 700,
              letterSpacing: '0.5px',
              px: 2,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                // Hover effects removed
              },
              '&:active': {
                // Active effects removed
              },
              // Transitions removed
            }}
          >
            View Logs
          </Button>
        </Box>
      </Box>

      <Box sx={{ width: '100%' }}>
        <Card sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700, letterSpacing: '0.08em', color: 'text.primary' }}>
            Transaction Information
          </Typography>
          <Grid container rowSpacing={0.4} columnSpacing={{ xs: 1, sm: 1, md: 1.5 }} alignItems="stretch">
            <GridLabel label="Transaction ID" value={transaction?.transactionId} xs={12} sm={6} md={3} />
            <GridLabel
              xs={12}
              sm={6}
              md={3}
              label="ChargerBox ID"
              value={transaction?.charge_box_pk ? (
                <Link
                  component={RouterLink}
                  to={`/charger/${transaction.charge_box_pk}`}
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 400,
                    fontSize: '1.1rem',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {transaction?.chargepoint_id || transaction?.charge_box_pk}
                </Link>
              ) : (
                transaction?.chargepoint_id ?? '--'
              )}
            />
            <GridLabel xs={12} sm={6} md={3} label="Chargepoint Name" value={transaction?.chargepoint_name} />
            <GridLabel xs={12} sm={6} md={3} label="Connector ID" value={transaction?.connectorId} />
            <GridLabel xs={12} sm={6} md={3} label="Email" value={transaction?.email} />
            <GridLabel xs={12} sm={6} md={3} label="Phone Number" value={transaction?.phone ? transaction?.phone : "-"} />
            <GridLabel xs={12} sm={6} md={3} label="OCPP ID Tag" value={transaction?.tag} />
          </Grid>
        </Card>

        <Card sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, letterSpacing: '0.08em', color: 'text.primary' }}>
            Charging Session
          </Typography>
          <Grid container rowSpacing={0.4} columnSpacing={{ xs: 1, sm: 1, md: 1.5 }} alignItems="stretch">
            <GridLabel xs={12} sm={6} md={3} label="Start Time" value={fDateTime(transaction?.starttime, "yyyy-MM-dd hh:mm a")} />
            <GridLabel xs={12} sm={6} md={3} label="Stop Time" value={fDateTime(transaction?.stoptime, "yyyy-MM-dd hh:mm a")} />
            <GridLabel xs={12} sm={6} md={3} label="Start Meter Value" value={transaction?.start_meter_value} />
            <GridLabel xs={12} sm={6} md={3} label="Current Meter Value" value={transaction?.current_meter_value} />
            <GridLabel xs={12} sm={6} md={3} label="Stop Meter Value" value={transaction?.stop_meter_value} />
            <GridLabel xs={12} sm={6} md={3} label="Start SoC" value={transaction?.startSoC !== undefined && transaction?.startSoC !== null ? `${transaction.startSoC}${String(transaction.startSoC).includes("%") ? "" : "%"}` : "--"} />
            <GridLabel xs={12} sm={6} md={3} label="Last SoC" value={transaction?.lastReceivedSoC !== undefined && transaction?.lastReceivedSoC !== null ? `${transaction.lastReceivedSoC}${String(transaction.lastReceivedSoC).includes("%") ? "" : "%"}` : "--"} />
            <GridLabel xs={12} sm={6} md={3} label="kWh Consumed" value={(() => {
              const startValue = parseFloat(transaction?.start_meter_value || 0);
              const stopValue = parseFloat(transaction?.stop_meter_value || 0);
              const currentValue = parseFloat(transaction?.current_meter_value || 0);
              const endValue = stopValue > 0 ? stopValue : currentValue;
              if (endValue > startValue) {
                const kwConsumed = (endValue - startValue) / 1000;
                return `${kwConsumed.toFixed(3)} kWh`;
              }
              return "0.000 kWh";
            })()} />
            <GridLabel xs={12} sm={6} md={3} label="Total kW Consumed" value={transaction?.stop_meter_value ? Math.abs(transaction?.stop_meter_value - transaction?.start_meter_value) / 1000 : "--"} />
            <GridLabel xs={12} sm={6} md={3} label="Stop Reason" value={transaction?.stop_meter_value ? transaction?.stop_reason ? transaction?.stop_reason : "Remote" : "--"} />
          </Grid>
        </Card>

        <Card sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, letterSpacing: '0.08em', color: 'text.primary' }}>
            Payment Information
          </Typography>
          <Grid container rowSpacing={0.4} columnSpacing={{ xs: 1, sm: 1, md: 1.5 }} alignItems="stretch">
            {initialAmountLabel && (
              <GridLabel
                xs={12}
                sm={6}
                md={3}
                label={initialAmountLabel}
                value={transaction?.totalAmountPaid !== undefined && transaction?.totalAmountPaid !== null ? `₹${Number(transaction.totalAmountPaid).toFixed(2)}` : "--"}
              />
            )}
            <GridLabel 
              xs={12} 
              sm={6} 
              md={3} 
              label="Consumption Cost" 
              value={transaction?.totalAmountCharged !== undefined && transaction?.totalAmountCharged !== null ? 
                `₹${(parseFloat(transaction?.totalAmountCharged || 0) - parseFloat(transaction?.taxAmount || 0) - parseFloat(transaction?.transactionAmount || 0)).toFixed(2)}` 
                : "₹0.00"} 
            />
            <GridLabel 
              xs={12} 
              sm={6} 
              md={3} 
              label="Tax Amount" 
              value={transaction?.taxAmount !== undefined && transaction?.taxAmount !== null ? 
                `₹${Number(transaction.taxAmount).toFixed(2)}` 
                : "₹0.00"} 
            />
            <GridLabel 
              xs={12} 
              sm={6} 
              md={3} 
              label="PG Charges" 
              value={transaction?.transactionAmount !== undefined && transaction?.transactionAmount !== null ? 
                `₹${Number(transaction.transactionAmount).toFixed(2)}` 
                : "₹0.00"} 
            />
            <GridLabel xs={12} sm={6} md={3} label="Total Charging Cost" value={transaction?.totalAmountCharged !== undefined && transaction?.totalAmountCharged !== null ? (transaction?.totalAmountCharged.startsWith?.('₹') ? transaction.totalAmountCharged : `₹${Number(transaction.totalAmountCharged).toFixed(2)}`) : "₹0.00"} />
            {remainingAmountLabel && (
              <GridLabel
                xs={12}
                sm={6}
                md={3}
                label={remainingAmountLabel}
                value={transaction?.totalRefund !== undefined && transaction?.totalRefund !== null ? `₹${Number(transaction.totalRefund).toFixed(2)}` : "--"}
              />
            )}
            <GridLabel xs={12} sm={6} md={3} label="Price" value={(() => {
              const priceperkw = transaction?.priceperkw || transaction?.pricePerKw || transaction?.priceperKW || transaction?.price_per_kw;
              if (priceperkw && parseFloat(priceperkw) > 0) {
                return `₹${parseFloat(priceperkw).toFixed(2)}/kWh`;
              }
              const pricepermin = transaction?.pricepermin || transaction?.pricePerMin || transaction?.priceperMin || transaction?.price_per_min;
              if (pricepermin && parseFloat(pricepermin) > 0) {
                return `₹${parseFloat(pricepermin).toFixed(2)}/min`;
              }
              return "--";
            })()} />
            <GridLabel xs={12} sm={6} md={3} label="Payment Type" value={transaction?.paymentDescription === "QRv2 Payment" || transaction?.paymentDescription?.toUpperCase().includes("UPI") ? "UPI" : transaction?.payment_type} />
            <GridLabel
              xs={12}
              sm={6}
              md={3}
              label="Payment ID"
              value={transaction?.paymentId ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Link
                    component={RouterLink}
                    to={`/refund-details/${transaction.paymentId}`}
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontWeight: 400,
                      fontSize: '1.1rem',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {transaction.paymentId}
                  </Link>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigator.clipboard.writeText(transaction.paymentId)}
                    sx={{
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: 'divider',
                    }}
                  >
                    Copy
                  </Button>
                </Box>
              ) : (
                <span style={{ color: '#999' }}>—</span>
              )}
            />
            {isRefundDetail && (
              <GridLabel xs={12} sm={6} md={3} label="Refund Status" value={transaction?.refundStatus.toUpperCase()} />
            )}
          </Grid>
        </Card>

        {/* Status History */}
        <Card
          sx={{
            mt: 1.5,
            p: 2,
            borderRadius: 2,
            background: `linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)`,
            boxShadow: `0 2px 12px rgba(0,0,0,0.06)`,
            border: `1px solid #e0e0e0`,
          }}
        >
          <Divider sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, px: 2, color: "text.primary" }}
            >
              Status History
            </Typography>
          </Divider>

          {txStatusHistoryError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{txStatusHistoryError}</Alert>
          ) : txStatusHistoryLoading && txStatusHistory.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={36} />
            </Box>
          ) : (
            <BaseTable
              rows={normalizedTxStatusHistory}
              columns={txStatusHistoryColumns}
              loading={txStatusHistoryLoading}
              getRowId={(row) => row.id}
              showExportCsvBtn={false}
              isReadOnly
              paginationMode="server"
              rowCount={txStatusHistoryTotal}
              page={txStatusHistoryPage}
              pageSize={txStatusHistoryPageSize}
              rowsPerPageOptions={[10, 20, 50]}
              onPageChange={(newPage) => setTxStatusHistoryPage(newPage)}
              onPageSizeChange={(newSize) => {
                setTxStatusHistoryPageSize(newSize);
                setTxStatusHistoryPage(0);
              }}
            />
          )}
        </Card>
      </Box>

      <Box mt={1} p={0.5} sx={{ display: "flex", placeContent: "end", gap: "0.75rem" }}>
        <Button
          variant="contained"
          color={showMeterValues ? "error" : "primary"}
          onClick={() => setShowMeterValues(!showMeterValues)}
          endIcon={<Icon icon={showMeterValues ? upIcon : downIcon}></Icon>}
          sx={{
            background: showMeterValues
              ? 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)'
              : 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
            boxShadow: showMeterValues
              ? '0 4px 14px 0 rgba(211, 47, 47, 0.39)'
              : '0 4px 14px 0 rgba(46, 125, 50, 0.39)',
            fontWeight: 700,
            letterSpacing: '0.5px',
            px: 2,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': {
              // Hover effects removed
            },
            '&:active': {
              // Active effects removed
            },
            // Transitions removed
          }}
        >
          {showMeterValues
            ? "Hide Charger Statistics"
            : "Show Charger Statistics"}
        </Button>
      </Box>
      {showMeterValues && chartData && chartData.length > 0 && (
        <Stack>
          <Grid container spacing={1}>
            {chartData.map(({ label, xaxis, yaxis, unit, measurand }) => {
              if (chartTypeToShow.includes(measurand)) {
                let series = [
                  {
                    name: measurand,
                    data: yaxis,
                  },
                ];
                let options = {
                  chart: {
                    height: 200,
                    width: 300,
                    type: "line",
                    zoom: {
                      enabled: false,
                    },
                  },
                  dataLabels: {
                    enabled: false,
                  },
                  stroke: {
                    curve: "smooth",
                    width: 1,
                    lineCap: "butt",
                  },
                  title: {
                    text: label,
                    align: "left",
                  },
                  grid: {
                    row: {
                      colors: ["#C8FACD"],
                      opacity: 0.5,
                    },
                  },
                  xaxis: {
                    categories: xaxis,
                    type: "datetime",
                    timezone:
                      Intl.DateTimeFormat().resolvedOptions().timeZone,
                    labels: {
                      formatter: (value) => onlyTime(value),
                      rotate: 0,
                      maxWidth: 10,
                    },
                    title: {
                      text: "Time",
                      style: {
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "grey",
                        marginTop: "2px",
                      },
                    },
                  },
                  yaxis: {
                    title: {
                      text: `${unit}`,
                      style: {
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "grey",
                      },
                    },
                  },
                };

                return (
                  <Grid key={`chart-${label}`} item xs={12} md={6} sm={6} id={`chart-${label}`}>
                    <BaseChart
                      options={options}
                      series={series}
                      type="line"
                    />
                  </Grid>
                );
              }
              return <div key={`empty-${label}`}></div>;
            })}
          </Grid>
        </Stack>
      )}

      <Divider textAlign="left" variant="fullWidth" sx={{ mb: 2, mt: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            px: 2,
            py: 1,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            textAlign: 'left'
          }}
        >
          Transaction Status & Notes
        </Typography>
      </Divider>
      <FormikProvider value={formik}>
        <Form
          autoComplete="off"
          id={`transaction-status-form-${id}`}
          noValidate
          onSubmit={handleSubmit}
        >
          <Grid
            container
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 1.5, md: 2 }}
            direction="row"
          >
            <Grid item xs={12} sm={12} md={12} >
              <FormControl
                fullWidth
                size="medium"
                error={touched.status && errors.status}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }}
              >
                <InputLabel id={`transaction-status-${id}`} sx={{ fontWeight: 600 }}>
                  Transaction Status
                </InputLabel>
                <Select
                  labelId={`transaction-status-${id}`}
                  id={`transaction-status-${id}`}
                  disabled={
                    hasReadOnlyAccess ||
                    values.status === "CAPTURED" ||
                    values.status === "AUTHORIZED"

                  }
                  value={values.status}
                  onChange={(e) => setFieldValue("status", e.target.value)}
                  label="Transaction Status"
                  {...getFieldProps("status")}
                >
                  <MenuItem selected value="None" disabled>
                    <em>None</em>
                  </MenuItem>
                  {TransactionStatus.map(
                    ({ label, value, disabled }, index) => {
                      return <MenuItem
                        key={`transaction-status-${index}-${label}`}
                        value={value}
                        disabled={disabled}
                      >
                        {label}
                      </MenuItem>
                    }
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={12} padding={1}>
              <FormControl
                fullWidth
              >
                <TextField
                  disabled={hasReadOnlyAccess}
                  multiline
                  sx={{
                    width: "100%",
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: 2,
                      },
                    },
                  }}
                  rows={4}
                  id="transaction-notes"
                  label="Transaction Notes"
                  placeholder="Enter any additional notes about this transaction..."
                  {...getFieldProps("notes")}
                  error={Boolean(touched.notes && errors.notes)}
                  helperText={touched.notes && errors.notes}
                />
              </FormControl>
            </Grid>

            {hasAllAccess &&
              <Grid item xs={12} sm={12} md={12} padding={1}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="left"
                >
                  <LoadingButton
                    size="large"
                    type="submit"
                    variant="contained"
                    loading={isSubmitting || isLoading}
                    sx={{
                      background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                      boxShadow: '0 4px 14px 0 rgba(46, 125, 50, 0.39)',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
                        boxShadow: '0 6px 20px 0 rgba(46, 125, 50, 0.5)',
                        transform: 'translateY(-2px)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&.Mui-disabled': {
                        background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
                        color: 'text.disabled',
                        boxShadow: 'none',
                      },
                      // Transitions removed
                    }}
                  >
                    {isSubmitting || isLoading ? 'Updating...' : 'Update Transaction'}
                  </LoadingButton>
                </Stack>
              </Grid>
            }
          </Grid>

        </Form>
      </FormikProvider>
    </Page>
  );
};
